import { filterRealPersonaPlatforms, isRealPersonaPlatform } from '../lib/platforms';
import { redactSensitiveText, redactedJson } from '../lib/redaction';
import type { PromptControlPlaneCopy } from './copy';

export type PromptStatusTone = 'green' | 'blue' | 'yellow' | 'red' | 'muted';

export type PromptStatusItem = {
  label: string;
  value: string;
  evidence: string;
  tone?: PromptStatusTone;
};

export type PromptControlPlaneSources = {
  moduleList?: unknown;
  errors?: Record<string, string>;
};

export type PromptModuleRow = {
  id: string;
  name: string;
  displayName: string;
  classification: 'core' | 'builtin default' | 'managed override' | 'managed';
  scope: string;
  status: string;
  contentSource: string;
  readable: boolean;
  mutable: boolean;
  overrideAllowed: boolean;
  disableAllowed: boolean;
  enabled: boolean;
  publishedVersion: number;
  overrideArtifactId: string;
  overrideVersion: number;
  runtimeApplied: boolean;
  detailHref: string;
  evidence: string;
};

export type PromptControlPlaneModel = {
  header: {
    title: string;
    description: string;
    meta: Array<[string, string]>;
  };
  status: PromptStatusItem[];
  modules: PromptModuleRow[];
  rawJson: string;
};

export type PromptAssignmentSources = {
  personaId: string;
  platform?: string;
  persona?: unknown;
  assignment?: unknown;
  errors?: Record<string, string>;
};

export type PromptAssignmentModuleRow = {
  name: string;
  displayName: string;
  origin: string;
  scope: string;
  enabled: boolean;
  configurable: boolean;
  assignmentSource: string;
  version: number;
  inherited: boolean;
  platformOverride: boolean;
  state: string;
  evidence: string;
};

export type PromptAssignmentSourceStateCode = 'baseline' | 'inherited' | 'platform_override' | 'platform_assignment';

export type PromptAssignmentModel = {
  personaId: string;
  personaName: string;
  platform: string;
  platforms: string[];
  scopeLabel: string;
  sourceStateCode: PromptAssignmentSourceStateCode;
  sourceState: string;
  sourceEvidence: string;
  draftRevision: string;
  draftChanged: boolean;
  modules: PromptAssignmentModuleRow[];
  status: PromptStatusItem[];
  rawJson: string;
};

export type PromptModuleDetailSources = {
  moduleId: string;
  moduleList?: unknown;
  detail?: unknown;
  preview?: unknown;
  versions?: unknown;
  consumers?: unknown;
  errors?: Record<string, string>;
};

export type PromptConsumerRow = {
  key: string;
  personaId: string;
  personaName: string;
  configHref: string;
  platform: string;
  scope: string;
  currentReferencedVersion: number;
  previousReferencedVersion: number;
  latestPublishedVersion: number;
  runtimeAppliedVersion: number;
  enabled: boolean;
  status: string;
  requiredAction: string;
  contentSource: string;
  referenceSource: string;
  behindLatest: boolean;
  runtimeState: string;
  rollbackToBuiltinDefault: boolean;
};

export type PromptEvidenceCandidate = {
  key: string;
  personaId: string;
  personaName: string;
  platform: string;
  currentReferencedVersion: number;
  previousReferencedVersion: number;
  latestPublishedVersion: number;
  rollbackToBuiltinDefault: boolean;
};

export type PromptModuleActionKind =
  | 'create_override'
  | 'create_version'
  | 'publish'
  | 'rollout_all'
  | 'rollout_selected'
  | 'rollback'
  | 'rollback_builtin_default'
  | 'destructive';

export type PromptArtifactLifecycleKind = 'save_draft' | 'archive' | 'delete';

export type PromptArtifactDraftInput = {
  markdown?: string;
  priority?: number;
  displayName?: string;
  description?: string;
  conditions?: PromptConditionsForm;
  userEditedMarkdown?: boolean;
};

export type PromptArtifactCreateInput = PromptArtifactDraftInput & {
  name?: string;
};

export type PromptContentPanel = {
  title: string;
  markdown: string;
  source: string;
};

export type PromptContentModel = {
  primaryMarkdown: string;
  primarySource: string;
  builtinDefaultMarkdown: string;
  managedOverrideMarkdown: string;
  replacementEvidence: string;
  secretScan: { passed: boolean; redactedCount: number; evidence: string };
};

export type PromptEditorModel = {
  editable: boolean;
  markdown: string;
  priority: number;
  baseRevision: number;
  displayName: string;
  description: string;
  status: string;
  classification: PromptModuleRow['classification'];
  scope: string;
  overrideTarget: string;
  conditions: PromptConditionsForm;
  plaintextTrusted: boolean;
  contractGap: string;
};

export type PromptConditionsForm = {
  platforms: string[];
  agents: string[];
};

export type PromptDraftJsonDrawerModel = {
  draftJson: string;
  publishedJson: string;
  diffText: string;
};

export type PromptDraftJsonInput = {
  markdown?: string;
  priority?: number;
  conditions?: PromptConditionsForm;
};

export type PromptMutationPreview = {
  kind: string;
  title: string;
  available: boolean;
  confirmationKey: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  body: Record<string, unknown>;
  bodyJson: string;
  evidence: string;
  contractGap: string;
  candidates: PromptEvidenceCandidate[];
  followUp?: {
    method: 'PUT';
    endpointTemplate: string;
    body: Record<string, unknown>;
    bodyJson: string;
  };
};

export type PromptModuleDetailModel = {
  moduleId: string;
  name: string;
  displayName: string;
  classification: PromptModuleRow['classification'];
  status: PromptStatusItem[];
  meta: Array<[string, string]>;
  content: PromptContentModel;
  contentPanels: PromptContentPanel[];
  editor: PromptEditorModel;
  draftJsonDrawer: PromptDraftJsonDrawerModel;
  versions: Array<Record<string, unknown>>;
  consumers: PromptConsumerRow[];
  evidence: {
    createOverride: { available: boolean; evidence: string };
    createVersion: { available: boolean; evidence: string };
    publishOnly: { available: boolean; version: number; evidence: string };
    rolloutAll: { available: boolean; version: number; consumerCount: number; evidence: string };
    selectiveRollout: { available: boolean; candidates: PromptEvidenceCandidate[]; evidence: string };
    rollbackConsumers: { available: boolean; candidates: PromptEvidenceCandidate[]; evidence: string };
    rollbackToBuiltinDefault: { available: boolean; candidates: PromptEvidenceCandidate[]; evidence: string };
    destructive: { available: boolean; evidence: string };
    assignmentMutation: { available: boolean; evidence: string };
  };
  previewSummary: Array<[string, string]>;
  rawJson: string;
};

export function buildPromptControlPlaneModel(sources: PromptControlPlaneSources, copy: PromptControlPlaneCopy): PromptControlPlaneModel {
  const moduleList = asRecord(sources.moduleList);
  const builtinItems = getArray(moduleList.builtin_prompt_modules).map(item => asRecord(item));
  const builtinRows = builtinItems.map(item => builtinModuleRow(item, copy));
  const overrideIds = new Set(builtinItems.map(item => getString(asRecord(item.override).artifact_id)).filter(Boolean));
  const managedRows = getArray(moduleList.prompt_modules)
    .map(item => managedModuleRow(asRecord(item), copy))
    .filter(row => row.classification !== 'managed override' || !overrideIds.has(row.id));
  const modules = [...builtinRows, ...managedRows];
  const overrideCount = modules.filter(module => module.classification === 'managed override').length;
  const mutableCount = modules.filter(module => module.mutable).length;
  const activeCount = modules.filter(module => module.enabled).length;

  return {
    header: {
      title: copy.header.title,
      description: copy.header.description,
      meta: [
        [copy.header.meta.kind, copy.header.meta.kindValue],
        [copy.header.meta.source, copy.header.meta.sourceValue],
        [copy.header.meta.mutationPolicy, copy.header.meta.mutationPolicyValue],
        [copy.header.meta.plaintext, copy.header.meta.plaintextValue],
      ],
    },
    status: [
      { label: copy.status.modules, value: String(modules.length), evidence: copy.status.builtinManagedEvidence(builtinRows.length, managedRows.length), tone: modules.length ? 'green' : 'muted' },
      { label: copy.status.overrides, value: String(overrideCount), evidence: copy.status.sameNameReplacement, tone: overrideCount ? 'blue' : 'muted' },
      { label: copy.status.mutable, value: String(mutableCount), evidence: copy.status.coreImmutableEvidence, tone: mutableCount ? 'yellow' : 'green' },
      { label: copy.status.active, value: String(activeCount), evidence: copy.status.liveRows, tone: activeCount ? 'green' : 'muted' },
    ],
    modules,
    rawJson: redactedJson(sources, { redactPromptContent: true }),
  };
}

export function buildPromptAssignmentModel(sources: PromptAssignmentSources, copy: PromptControlPlaneCopy): PromptAssignmentModel {
  const assignment = asRecord(sources.assignment);
  const persona = asRecord(sources.persona);
  const source = asRecord(assignment.prompt_module_source);
  const platform = isRealPersonaPlatform(sources.platform) ? String(sources.platform).toLowerCase() : '';
  const sourceStateCode = assignmentSourceStateCode(source, platform);
  const sourceState = assignmentSourceStateLabel(sourceStateCode, copy);
  const modules = getArray(assignment.modules).map(item => assignmentModuleRow(asRecord(item), sourceStateCode, platform, copy));
  const personaName = getString(assignment.persona_name) || getString(persona.name) || sources.personaId;
  const platforms = extractPlatforms(persona.platforms, platform);
  const enabledCount = modules.filter(module => module.enabled).length;
  const draftChanged = getBool(assignment.draft_changed);

  return {
    personaId: sources.personaId,
    personaName,
    platform,
    platforms,
    scopeLabel: platform ? copy.assignments.platformScope(platform) : copy.assignments.defaultScope,
    sourceStateCode,
    sourceState,
    sourceEvidence: assignmentSourceEvidence(source, copy),
    draftRevision: getString(assignment.draft_revision),
    draftChanged,
    modules,
    status: [
      { label: copy.status.assignment, value: sourceState, evidence: assignmentSourceEvidence(source, copy), tone: sourceStateCode === 'platform_override' ? 'blue' : sourceStateCode === 'inherited' ? 'yellow' : 'green' },
      { label: copy.status.modules, value: copy.status.enabledCount(enabledCount, modules.length), evidence: copy.status.draftAssignmentView, tone: enabledCount ? 'green' : 'muted' },
      { label: copy.status.draft, value: draftChanged ? copy.status.changed : copy.status.clean, evidence: copy.status.revision(getString(assignment.draft_revision) || copy.common.unavailable.toLowerCase()), tone: draftChanged ? 'yellow' : 'green' },
      { label: copy.status.platformUi, value: platform || copy.common.defaultScope, evidence: copy.status.realPlatformsOnly, tone: platform ? 'blue' : 'muted' },
    ],
    rawJson: redactedJson(sources, { redactPromptContent: true }),
  };
}

export function buildPromptModuleDetailModel(sources: PromptModuleDetailSources, copy: PromptControlPlaneCopy): PromptModuleDetailModel {
  const detail = asRecord(sources.detail);
  const manifest = firstRecord(asRecord(detail.manifest), asRecord(asRecord(sources.consumers).module), findModuleManifest(sources.moduleList, sources.moduleId));
  const versions = getArray(asRecord(sources.versions).versions).map(item => asRecord(item));
  const consumers = getArray(asRecord(sources.consumers).consumers).map(item => consumerRow(asRecord(item), getString(manifest.override_target), copy));
  const latestVersion = latestPublishedVersion(manifest, versions, consumers);
  const classification = detailClassification(manifest, sources.moduleId);
  const displayName = getString(manifest.display_name) || formatDisplayName(getString(manifest.name) || sources.moduleId);
  const preview = asRecord(sources.preview);
  const builtinManifest = findBuiltinManifest(sources.moduleList, getString(manifest.override_target) || getString(manifest.name) || sources.moduleId);
  const publishedSpec = latestPublishedSpec(manifest, versions);
  const previewSpec = previewToPromptSpec(preview);
  const draftSpec = firstRecord(previewSpec, asRecord(asRecord(detail.draft).spec), publishedSpec);
  const builtinDefaultSourceMarkdown = getString(builtinManifest.default_content);
  const draftSourceMarkdown = getString(draftSpec.content);
  const publishedSourceMarkdown = getString(publishedSpec.content);
  const builtinDefaultMarkdown = safePromptMarkdown(builtinDefaultSourceMarkdown);
  const draftMarkdown = safePromptMarkdown(draftSourceMarkdown);
  const primarySourceMarkdown = classification === 'builtin default' || classification === 'core'
    ? builtinDefaultSourceMarkdown || draftSourceMarkdown
    : draftSourceMarkdown || publishedSourceMarkdown;
  const primaryMarkdown = safePromptMarkdown(primarySourceMarkdown);
  const managedOverrideMarkdown = classification === 'managed override' ? primaryMarkdown : '';
  const primarySource = classification === 'managed override' ? 'managed override' : classification === 'managed' ? 'managed module' : 'builtin default';
  const contentPanels = promptContentPanels({ classification, primaryMarkdown, primarySource, builtinDefaultMarkdown, managedOverrideMarkdown, copy });
  const secretScan = promptSecretScan(contentPanels.map(panel => panel.markdown).join('\n\n'));
  const editorEditable = classification === 'managed' || classification === 'managed override';
  const editorMarkdown = draftSourceMarkdown || primarySourceMarkdown;
  const editorPlaintextTrusted = !looksLikeRedactedPrompt(editorMarkdown);
  const manifestPolicy = asRecord(manifest.policy);
  const overrideTarget = getString(manifest.override_target);
  const editor: PromptEditorModel = {
    editable: editorEditable,
    markdown: editorMarkdown,
    priority: getNumber(draftSpec.priority) || getNumber(publishedSpec.priority) || getNumber(preview.priority) || getNumber(manifest.priority),
    baseRevision: getNumber(preview.revision) || getNumber(asRecord(detail.draft).revision),
    displayName,
    description: getString(manifest.description),
    status: getString(manifest.status) || (classification === 'builtin default' || classification === 'core' ? 'builtin' : copy.common.unavailable),
    classification,
    scope: getString(manifestPolicy.scope) || (classification === 'core' ? 'core' : classification === 'builtin default' ? 'builtin' : 'project'),
    overrideTarget,
    conditions: conditionsForm(draftSpec.conditions),
    plaintextTrusted: editorPlaintextTrusted,
    contractGap: editorEditable
      ? (editorPlaintextTrusted ? '' : copy.contractGaps.redactedEditorSourceUnsafe)
      : classification === 'core' ? copy.contractGaps.coreImmutable : copy.contractGaps.builtinDefaultReadOnly,
  };
  const draftJson = redactedJson(promptSpecForJson(editor.markdown, editor.priority, editor.conditions));
  const publishedJson = redactedJson(Object.keys(publishedSpec).length ? publishedSpec : promptSpecForJson(primaryMarkdown, editor.priority, undefined));
  const draftJsonDrawer = { draftJson, publishedJson, diffText: diffJsonLines(publishedJson, draftJson) };
  const publishAvailable = versions.length > 0 || latestVersion > 0;
  const canCreateOverride = classification === 'builtin default' && getBool(asRecord(manifest.policy).override_allowed);
  const canCreateVersion = (classification === 'managed' || classification === 'managed override') && previewAvailable(preview);
  const rolloutCandidates = consumers.filter(consumer => consumer.requiredAction === 'rollout_required' || consumer.status === 'behind_latest');
  const rollbackCandidates = consumers.filter(consumer => consumer.previousReferencedVersion > 0 || consumer.rollbackToBuiltinDefault);
  const builtinRollbackCandidates = rollbackCandidates.filter(consumer => consumer.rollbackToBuiltinDefault);

  return {
    moduleId: sources.moduleId,
    name: getString(manifest.name) || sources.moduleId,
    displayName,
    classification,
    status: [
      { label: copy.status.classification, value: classification, evidence: classificationEvidence(classification, copy), tone: classification === 'core' ? 'green' : classification === 'managed override' ? 'blue' : 'muted' },
      { label: copy.status.published, value: latestVersion > 0 ? `v${latestVersion}` : copy.status.unpublished, evidence: copy.status.artifactVersions, tone: latestVersion > 0 ? 'green' : 'yellow' },
      { label: copy.status.consumers, value: String(consumers.length), evidence: copy.status.behindLatest(rolloutCandidates.length), tone: rolloutCandidates.length ? 'yellow' : consumers.length ? 'green' : 'muted' },
      { label: copy.status.preview, value: previewAvailable(preview) ? copy.status.available : copy.common.unavailable, evidence: copy.status.promptRedacted, tone: previewAvailable(preview) ? 'blue' : 'muted' },
    ],
    meta: [
      [copy.detail.moduleId, getString(manifest.id) || sources.moduleId],
      [copy.detail.name, getString(manifest.name) || sources.moduleId],
      [copy.detail.status, getString(manifest.status) || copy.common.unavailable],
      [copy.detail.overrideTarget, overrideTarget || copy.common.none],
    ],
    content: {
      primaryMarkdown,
      primarySource,
      builtinDefaultMarkdown,
      managedOverrideMarkdown,
      replacementEvidence: overrideTarget ? copy.modelEvidence.replacesBuiltin(overrideTarget) : copy.modelEvidence.codeDefault,
      secretScan,
    },
    contentPanels,
    editor,
    draftJsonDrawer,
    versions,
    consumers,
    evidence: {
      createOverride: { available: canCreateOverride, evidence: canCreateOverride ? copy.modelEvidence.createOverride : copy.contractGaps.createOverrideUnavailable },
      createVersion: { available: canCreateVersion, evidence: canCreateVersion ? copy.modelEvidence.createVersion : copy.contractGaps.createVersionPreviewMissing },
      publishOnly: { available: publishAvailable, version: latestVersion, evidence: publishAvailable ? copy.modelEvidence.publish(latestVersion) : copy.modelEvidence.noVersion },
      rolloutAll: { available: consumers.length > 0 && latestVersion > 0, version: latestVersion, consumerCount: consumers.length, evidence: copy.modelEvidence.rolloutAll },
      selectiveRollout: { available: rolloutCandidates.length > 0 && latestVersion > 0, candidates: rolloutCandidates.map(evidenceCandidate), evidence: copy.modelEvidence.selectiveRollout },
      rollbackConsumers: { available: rollbackCandidates.length > 0, candidates: rollbackCandidates.map(evidenceCandidate), evidence: copy.modelEvidence.rollback },
      rollbackToBuiltinDefault: { available: builtinRollbackCandidates.length > 0, candidates: builtinRollbackCandidates.map(evidenceCandidate), evidence: copy.modelEvidence.rollbackBuiltin },
      destructive: { available: false, evidence: copy.contractGaps.destructivePreviewMissing },
      assignmentMutation: { available: false, evidence: copy.contractGaps.promptUsageReadOnly },
    },
    previewSummary: [
      [copy.detail.previewContent, previewAvailable(preview) ? (getNumber(preview.content_length) ? copy.common.charsRedacted(getNumber(preview.content_length)) : copy.common.knownChars) : copy.common.unavailable],
      [copy.detail.previewValidation, validationSummary(preview.validation, copy)],
      [copy.detail.previewRevision, getString(preview.revision) || copy.common.unavailable],
    ],
    rawJson: redactedJson(promptDetailRawSources(sources)),
  };
}

export function buildPromptArtifactCreatePreview(input: PromptArtifactCreateInput, copy: PromptControlPlaneCopy): PromptMutationPreview {
  const name = getString(input.name).trim();
  const markdown = getString(input.markdown);
  const priority = Number.isFinite(input.priority) ? Number(input.priority) : 10;
  const reservedBuiltinName = isReservedBuiltinPromptName(name);
  const contractGap = reservedBuiltinName
    ? (name.toLowerCase() === 'core' ? copy.contractGaps.coreImmutable : copy.contractGaps.builtinDefaultReadOnly)
    : copy.contractGaps.createManagedMissingFields;
  const available = Boolean(name && markdown.trim() && !reservedBuiltinName);
  const spec = promptSpecForJson(markdown, priority, input.conditions);
  return mutationPreview({
    kind: 'prompt_module_create',
    title: copy.lifecycle.createManaged,
    available,
    method: 'POST',
    endpoint: '/prompt_modules',
    body: {
      name,
      display_name: getString(input.displayName) || formatDisplayName(name),
      description: getString(input.description),
      actor: 'console',
    },
    evidence: available ? copy.lifecycle.createEvidence : contractGap,
    contractGap: available ? '' : contractGap,
    followUp: {
      method: 'PUT',
      endpointTemplate: '/prompt_modules/{id}/draft',
      body: { spec, base_revision: 0, actor: 'console' },
      bodyJson: redactedJson({ spec, base_revision: 0, actor: 'console' }),
    },
  });
}

export function buildPromptDraftJsonDrawerModel(model: PromptModuleDetailModel, input: PromptDraftJsonInput = {}): PromptDraftJsonDrawerModel {
  const markdown = input.markdown ?? model.editor.markdown;
  const priority = Number.isFinite(input.priority) ? Number(input.priority) : model.editor.priority;
  const conditions = input.conditions ?? model.editor.conditions;
  const draftJson = redactedJson(promptSpecForJson(markdown, priority, conditions));
  const publishedJson = model.draftJsonDrawer.publishedJson;
  return { draftJson, publishedJson, diffText: diffJsonLines(publishedJson, draftJson) };
}

export function buildPromptArtifactLifecyclePreview(
  model: PromptModuleDetailModel,
  kind: PromptArtifactLifecycleKind,
  copy: PromptControlPlaneCopy,
  draftInput: PromptArtifactDraftInput = {},
): PromptMutationPreview {
	const immutableGap = model.classification === 'core' ? copy.contractGaps.coreImmutable : copy.contractGaps.builtinDefaultReadOnly;
	if (kind === 'save_draft') {
		const markdown = draftInput.markdown ?? model.editor.markdown;
		const priority = Number.isFinite(draftInput.priority) ? Number(draftInput.priority) : model.editor.priority;
		const conditions = draftInput.conditions ?? model.editor.conditions;
		const userEditedMarkdown = draftInput.userEditedMarkdown === true || (draftInput.markdown !== undefined && draftInput.markdown !== model.editor.markdown);
    const sourceTrusted = userEditedMarkdown || model.editor.plaintextTrusted;
    const available = model.editor.editable && model.editor.baseRevision >= 0 && markdown.trim().length > 0 && sourceTrusted;
    return mutationPreview({
      kind,
      title: copy.lifecycle.saveDraft,
      available,
      method: 'PUT',
      endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}/draft`,
			body: {
				spec: promptSpecForJson(markdown, priority, conditions),
				base_revision: model.editor.baseRevision,
        actor: 'console',
        ...(draftInput.displayName !== undefined ? { display_name: draftInput.displayName } : {}),
        ...(draftInput.description !== undefined ? { description: draftInput.description } : {}),
      },
      evidence: available ? copy.lifecycle.saveDraftEvidence : model.editor.contractGap || copy.contractGaps.createVersionPreviewMissing,
      contractGap: available ? '' : model.editor.contractGap || copy.contractGaps.createVersionPreviewMissing,
    });
  }
  if (model.classification === 'core' || model.classification === 'builtin default') {
    return mutationPreview({
      kind,
      title: kind === 'archive' ? copy.lifecycle.archive : copy.lifecycle.delete,
      available: false,
      method: kind === 'archive' ? 'PATCH' : 'DELETE',
      endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}${kind === 'delete' ? '?actor=console' : ''}`,
      body: kind === 'archive' ? { status: 'archived', actor: 'console' } : { actor: 'console' },
      evidence: immutableGap,
      contractGap: immutableGap,
    });
  }
  if (kind === 'archive') {
    return mutationPreview({
      kind,
      title: copy.lifecycle.archive,
      available: true,
      method: 'PATCH',
      endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}`,
      body: { status: 'archived', actor: 'console' },
      evidence: copy.lifecycle.destructivePreview,
      contractGap: '',
    });
  }
  return mutationPreview({
    kind,
    title: copy.lifecycle.delete,
    available: true,
    method: 'DELETE',
    endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}?actor=console`,
    body: { actor: 'console' },
    evidence: copy.lifecycle.destructivePreview,
    contractGap: '',
  });
}

export function buildPromptModuleActionControl(model: PromptModuleDetailModel, kind: PromptModuleActionKind, copy: PromptControlPlaneCopy, selectedKeys?: string[]): PromptMutationPreview {
  switch (kind) {
    case 'create_override': {
      return mutationPreview({
        kind,
        title: copy.detail.actions.createOverride,
        available: model.evidence.createOverride.available,
        method: 'POST',
        endpoint: `/prompt_modules/builtins/${encodeURIComponent(model.name)}/override`,
        body: { actor: 'console' },
        evidence: model.evidence.createOverride.evidence,
        contractGap: model.evidence.createOverride.available ? '' : model.evidence.createOverride.evidence,
      });
    }
    case 'create_version': {
      return mutationPreview({
        kind,
        title: copy.detail.actions.createVersion,
        available: model.evidence.createVersion.available,
        method: 'POST',
        endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}/versions`,
        body: { changelog: 'Console v2 guarded version', actor: 'console' },
        evidence: model.evidence.createVersion.evidence,
        contractGap: model.evidence.createVersion.available ? '' : model.evidence.createVersion.evidence,
      });
    }
    case 'publish': {
      return mutationPreview({
        kind,
        title: copy.detail.actions.publish,
        available: model.evidence.publishOnly.available,
        method: 'POST',
        endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}/publish`,
        body: { version: model.evidence.publishOnly.version, actor: 'console' },
        evidence: model.evidence.publishOnly.evidence,
        contractGap: model.evidence.publishOnly.available ? '' : copy.contractGaps.publishVersionMissing,
      });
    }
    case 'rollout_all': {
      return mutationPreview({
        kind,
        title: copy.detail.actions.rolloutAll,
        available: model.evidence.rolloutAll.available,
        method: 'POST',
        endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}/rollout`,
        body: { action: 'rollout_all', version: model.evidence.rolloutAll.version, actor: 'console' },
        evidence: `${model.evidence.rolloutAll.evidence}; ${copy.previews.selectedConsumers(model.evidence.rolloutAll.consumerCount)}`,
        contractGap: model.evidence.rolloutAll.available ? '' : copy.contractGaps.rolloutMissing,
      });
    }
    case 'rollout_selected': {
      const candidates = selectCandidates(model.evidence.selectiveRollout.candidates, selectedKeys);
      return mutationPreview({
        kind,
        title: copy.detail.actions.rolloutSelected,
        available: model.evidence.selectiveRollout.available && candidates.length > 0,
        method: 'POST',
        endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}/rollout`,
        body: { action: 'rollout_selected', version: model.evidence.publishOnly.version, actor: 'console', consumers: targetsFromCandidates(candidates) },
        evidence: `${model.evidence.selectiveRollout.evidence}; ${copy.previews.selected(candidates.length)}`,
        contractGap: model.evidence.selectiveRollout.available ? '' : copy.contractGaps.selectiveRolloutMissing,
        candidates,
      });
    }
    case 'rollback': {
      const rollbackCandidates = model.evidence.rollbackConsumers.candidates.filter(candidate => !candidate.rollbackToBuiltinDefault);
      const candidates = selectCandidates(rollbackCandidates, selectedKeys);
      return mutationPreview({
        kind,
        title: copy.detail.actions.rollback,
        available: candidates.length > 0,
        method: 'POST',
        endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}/rollout`,
        body: { action: 'rollback', actor: 'console', consumers: targetsFromCandidates(candidates) },
        evidence: `${model.evidence.rollbackConsumers.evidence}; ${copy.previews.selected(candidates.length)}`,
        contractGap: candidates.length > 0 ? '' : copy.contractGaps.rollbackMissing,
        candidates,
      });
    }
    case 'rollback_builtin_default': {
      const candidates = selectCandidates(model.evidence.rollbackToBuiltinDefault.candidates, selectedKeys);
      return mutationPreview({
        kind,
        title: copy.detail.actions.rollbackBuiltinDefault,
        available: model.evidence.rollbackToBuiltinDefault.available && candidates.length > 0,
        method: 'POST',
        endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}/rollout`,
        body: { action: 'rollback', actor: 'console', consumers: targetsFromCandidates(candidates) },
        evidence: `${model.evidence.rollbackToBuiltinDefault.evidence}; ${copy.previews.selected(candidates.length)}`,
        contractGap: model.evidence.rollbackToBuiltinDefault.available ? '' : copy.contractGaps.rollbackBuiltinMissing,
        candidates,
      });
    }
    case 'destructive':
      return mutationPreview({
        kind,
        title: copy.detail.actions.destructive,
        available: false,
        method: 'DELETE',
        endpoint: `/prompt_modules/${encodeURIComponent(model.moduleId)}`,
        body: { actor: 'console' },
        evidence: model.evidence.destructive.evidence,
        contractGap: model.evidence.destructive.evidence,
      });
  }
}

export function buildPromptAssignmentMutationPreview(model: PromptAssignmentModel, moduleName: string, nextEnabled: boolean, copy: PromptControlPlaneCopy): PromptMutationPreview {
  const module = model.modules.find(item => item.name === moduleName);
  if (!module) {
    return unavailableAssignmentPreview(model, 'assignment_row_unavailable', copy.contractGaps.assignmentRowMissing(moduleName), copy);
  }
  if (!module.configurable) {
    return unavailableAssignmentPreview(model, 'assignment_locked', copy.contractGaps.assignmentLocked(moduleName), copy);
  }
  const baseRevision = numericDraftRevision(model);
  if (baseRevision < 1) {
    return unavailableAssignmentPreview(model, 'draft_revision_unavailable', copy.contractGaps.draftRevisionMissing, copy);
  }

  const action = assignmentAction(module, nextEnabled);
  if (!action) {
    return unavailableAssignmentPreview(model, 'assignment_action_unavailable', copy.contractGaps.assignmentActionMissing(moduleName), copy);
  }
  const update: Record<string, unknown> = { name: module.name, action };
  if (action === 'assign' && module.version > 0) update.version = module.version;
  if (model.platform) update.platform = model.platform;

  return mutationPreview({
    kind: `assignment_${action}`,
    title: copy.assignments.edit.actionTitle(nextEnabled ? copy.assignments.edit.enableAction : copy.assignments.edit.disableAction, module.displayName),
    available: true,
    method: 'PUT',
    endpoint: `/persona_configs/${encodeURIComponent(model.personaId)}/prompt_module_assignments`,
    body: { updates: [update], base_revision: baseRevision, actor: 'console' },
    evidence: copy.modelEvidence.assignmentPreview(model.sourceEvidence, model.scopeLabel),
    contractGap: '',
  });
}

export function buildResetPlatformAssignmentPreview(model: PromptAssignmentModel, copy: PromptControlPlaneCopy): PromptMutationPreview {
  const baseRevision = numericDraftRevision(model);
  if (!model.platform) {
    return unavailableAssignmentPreview(model, 'reset_platform_required', copy.contractGaps.platformRequired, copy);
  }
  if (baseRevision < 1) {
    return unavailableAssignmentPreview(model, 'draft_revision_unavailable', copy.contractGaps.draftRevisionMissing, copy);
  }
  return mutationPreview({
    kind: 'assignment_reset_platform_override',
    title: copy.assignments.edit.reset,
    available: true,
    method: 'PUT',
    endpoint: `/persona_configs/${encodeURIComponent(model.personaId)}/prompt_module_assignments`,
    body: { updates: [{ action: 'reset_platform_override', platform: model.platform }], base_revision: baseRevision, actor: 'console' },
    evidence: copy.modelEvidence.resetPlatform(model.platform),
    contractGap: '',
  });
}

function builtinModuleRow(module: Record<string, unknown>, copy: PromptControlPlaneCopy): PromptModuleRow {
  const policy = asRecord(module.policy);
  const override = asRecord(module.override);
  const rawOverrideId = getString(override.artifact_id);
  const name = getString(module.name);
  const isCore = name === 'core' || getString(policy.scope) === 'core';
  const hasOverride = Boolean(rawOverrideId && !isCore);
  const classification = isCore ? 'core' : hasOverride ? 'managed override' : 'builtin default';
  const overrideVersion = hasOverride ? getNumber(override.version) : 0;

  return {
    id: hasOverride ? rawOverrideId : name,
    name,
    displayName: getString(module.display_name) || formatDisplayName(name),
    classification,
    scope: getString(policy.scope) || 'builtin',
    status: hasOverride ? getString(override.state) || getString(override.status) || 'override' : 'builtin',
    contentSource: hasOverride ? 'managed override' : 'builtin default',
    readable: true,
    mutable: hasOverride,
    overrideAllowed: !isCore && getBool(policy.override_allowed),
    disableAllowed: !isCore && getBool(policy.disable_allowed),
    enabled: true,
    publishedVersion: overrideVersion,
    overrideArtifactId: hasOverride ? rawOverrideId : '',
    overrideVersion,
    runtimeApplied: hasOverride && getBool(override.runtime_applied),
    detailHref: `#/prompts/${encodeURIComponent(hasOverride ? rawOverrideId : name)}`,
    evidence: hasOverride ? copy.modelEvidence.overrideArtifact(rawOverrideId, overrideVersion || 'draft') : copy.modelEvidence.codeDefault,
  };
}

function managedModuleRow(module: Record<string, unknown>, copy: PromptControlPlaneCopy): PromptModuleRow {
  const overrideTarget = getString(module.override_target);
  const name = getString(module.name);
  const publishedVersion = getNumber(module.published_version);

  return {
    id: getString(module.id) || name,
    name,
    displayName: getString(module.display_name) || formatDisplayName(name),
    classification: overrideTarget ? 'managed override' : 'managed',
    scope: 'project',
    status: getString(module.status) || 'unknown',
    contentSource: overrideTarget ? 'managed override' : 'managed module',
    readable: true,
    mutable: true,
    overrideAllowed: true,
    disableAllowed: true,
    enabled: getString(module.status) !== 'archived',
    publishedVersion,
    overrideArtifactId: overrideTarget ? getString(module.id) : '',
    overrideVersion: overrideTarget ? publishedVersion : 0,
    runtimeApplied: false,
    detailHref: `#/prompts/${encodeURIComponent(getString(module.id) || name)}`,
    evidence: overrideTarget ? copy.modelEvidence.replacesBuiltin(overrideTarget) : copy.modelEvidence.moduleAvailable(publishedVersion || copy.common.none),
  };
}

function assignmentModuleRow(module: Record<string, unknown>, sourceStateCode: PromptAssignmentSourceStateCode, platform: string, copy: PromptControlPlaneCopy): PromptAssignmentModuleRow {
  const assignmentSource = getString(module.assignment_source);
  const platformOverride = sourceStateCode === 'platform_override' || assignmentSource.includes('platform');
  const inherited = Boolean(platform) && sourceStateCode === 'inherited';
  return {
    name: getString(module.name),
    displayName: getString(module.display_name) || formatDisplayName(getString(module.name)),
    origin: getString(module.origin) || getString(asRecord(module.policy).origin) || 'unknown',
    scope: getString(module.scope) || getString(asRecord(module.policy).scope) || 'project',
    enabled: getBool(module.enabled),
    configurable: getBool(module.configurable),
    assignmentSource,
    version: getNumber(module.version),
    inherited,
    platformOverride,
    state: getString(module.state) || (getBool(module.enabled) ? 'enabled' : 'disabled'),
    evidence: inherited ? copy.modelEvidence.inheritedPromptModules : platformOverride ? copy.modelEvidence.platformStorage : assignmentSource || 'assignment API',
  };
}

function consumerRow(consumer: Record<string, unknown>, overrideTarget: string, copy: PromptControlPlaneCopy): PromptConsumerRow {
  const personaId = getString(consumer.persona_id);
  const personaName = getString(consumer.persona_name) || personaId;
  const platform = getString(consumer.platform);
  const currentReferencedVersion = getNumber(consumer.current_referenced_version);
  const previousReferencedVersion = getNumber(consumer.previous_referenced_version);
  const latestPublishedVersion = getNumber(consumer.latest_published_version);
  const runtimeAppliedVersion = getNumber(consumer.runtime_applied_version);
  const behindLatest = latestPublishedVersion > 0 && currentReferencedVersion > 0 && currentReferencedVersion < latestPublishedVersion;
  const rollbackToBuiltinDefault = Boolean(overrideTarget || getString(consumer.override_target)) && currentReferencedVersion > 0 && previousReferencedVersion === 0;

  return {
    key: `${personaId || personaName}:${platform || 'default'}`,
    personaId,
    personaName,
    configHref: `#/personas/${encodeURIComponent(personaName || personaId)}/workbench?tab=config`,
    platform,
    scope: getString(consumer.scope) || (platform ? 'platform' : 'default'),
    currentReferencedVersion,
    previousReferencedVersion,
    latestPublishedVersion,
    runtimeAppliedVersion,
    enabled: getBool(consumer.enabled),
    status: getString(consumer.status),
    requiredAction: getString(consumer.required_action),
    contentSource: getString(consumer.content_source),
    referenceSource: getString(consumer.reference_source),
    behindLatest,
    runtimeState: runtimeState(runtimeAppliedVersion, latestPublishedVersion, getBool(consumer.runtime_applied), copy),
    rollbackToBuiltinDefault,
  };
}

function findBuiltinManifest(moduleList: unknown, nameOrId: string) {
  const list = asRecord(moduleList);
  const builtins = getArray(list.builtin_prompt_modules).map(item => asRecord(item));
  const direct = builtins.find(module => getString(module.name) === nameOrId || getString(asRecord(module.override).artifact_id) === nameOrId);
  if (direct) return direct;
  return builtins.find(module => getString(module.name) === getString(asRecord(findModuleManifest(moduleList, nameOrId)).override_target)) ?? {};
}

function latestPublishedSpec(manifest: Record<string, unknown>, versions: Array<Record<string, unknown>>) {
  const publishedVersion = getNumber(manifest.published_version);
  const exact = versions.find(version => getNumber(version.version) === publishedVersion);
  const fallback = versions.reduce<Record<string, unknown>>((latest, version) => getNumber(version.version) > getNumber(latest.version) ? version : latest, {});
  return asRecord((exact ?? fallback).spec);
}

function previewToPromptSpec(preview: Record<string, unknown>) {
  if (!Object.keys(preview).length) return {};
  const spec: Record<string, unknown> = {};
  if (preview.content !== undefined) spec.content = preview.content;
  if (preview.priority !== undefined) spec.priority = preview.priority;
  if (preview.conditions !== undefined) spec.conditions = preview.conditions;
  return spec;
}

function safePromptMarkdown(value: string) {
  return redactSensitiveText(value || '');
}

function looksLikeRedactedPrompt(value: string) {
  return value.includes('[REDACTED]');
}

function promptContentPanels({
  classification,
  primaryMarkdown,
  primarySource,
  builtinDefaultMarkdown,
  managedOverrideMarkdown,
  copy,
}: {
  classification: PromptModuleRow['classification'];
  primaryMarkdown: string;
  primarySource: string;
  builtinDefaultMarkdown: string;
  managedOverrideMarkdown: string;
  copy: PromptControlPlaneCopy;
}): PromptContentPanel[] {
  if (classification === 'managed override') {
    return [
      { title: copy.detail.builtinDefaultTitle, markdown: builtinDefaultMarkdown, source: copy.modelEvidence.codeDefault },
      { title: copy.detail.managedOverrideTitle, markdown: managedOverrideMarkdown, source: primarySource },
    ].filter(panel => panel.markdown);
  }
  if (classification === 'managed') {
    return [{ title: copy.detail.managedContentTitle, markdown: primaryMarkdown, source: primarySource }];
  }
  return [{ title: copy.detail.builtinDefaultTitle, markdown: primaryMarkdown, source: copy.modelEvidence.codeDefault }];
}

function promptSecretScan(markdown: string) {
  const redactedCount = (markdown.match(/\[REDACTED\]/g) ?? []).length;
  return {
    passed: !/(gh[opsru]_[A-Za-z0-9_]+|sk-[A-Za-z0-9_-]+|Bearer\s+[A-Za-z0-9._~+/=-]+)/.test(markdown),
    redactedCount,
    evidence: redactedCount ? `${redactedCount} secret-like value(s) redacted` : 'No secret-like values detected',
  };
}

function promptDetailRawSources(sources: PromptModuleDetailSources) {
  const preview = asRecord(sources.preview);
  if (!Object.keys(preview).length || preview.content === undefined) return sources;
  return {
    ...sources,
    preview: {
      ...preview,
      content: '[REDACTED]',
    },
  };
}

function isReservedBuiltinPromptName(name: string) {
  return new Set([
    'core',
    'brainstorm',
    'tdd',
    'guardrail',
    'debugging',
    'testing_standards',
    'verification',
    'workspace_isolation',
    'persona_style',
  ]).has(name.trim().toLowerCase());
}

function conditionsForm(value: unknown): PromptConditionsForm {
  const conditions = asRecord(value);
  return {
    platforms: cleanStringList(conditions.platforms),
    agents: cleanStringList(conditions.agents),
  };
}

function cleanStringList(value: unknown) {
  return Array.from(new Set(getArray(value).map(item => getString(item).trim()).filter(Boolean)));
}

function promptSpecForJson(markdown: string, priority: number, conditions: unknown) {
  const spec: Record<string, unknown> = { content: markdown || '', priority };
  const normalizedConditions = conditionsForm(conditions);
  const conditionsJson: Record<string, unknown> = {};
  if (normalizedConditions.platforms.length) conditionsJson.platforms = normalizedConditions.platforms;
  if (normalizedConditions.agents.length) conditionsJson.agents = normalizedConditions.agents;
  if (Object.keys(conditionsJson).length) spec.conditions = conditionsJson;
  return spec;
}

function diffJsonLines(before: string, after: string) {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const max = Math.max(beforeLines.length, afterLines.length);
  const lines: string[] = [];
  for (let index = 0; index < max; index += 1) {
    const beforeLine = beforeLines[index];
    const afterLine = afterLines[index];
    if (beforeLine === afterLine) {
      if (beforeLine !== undefined) lines.push(` ${beforeLine}`);
      continue;
    }
    if (beforeLine !== undefined) lines.push(`-${beforeLine}`);
    if (afterLine !== undefined) lines.push(`+${afterLine}`);
  }
  return lines.join('\n');
}

function evidenceCandidate(consumer: PromptConsumerRow): PromptEvidenceCandidate {
  return {
    key: consumer.key,
    personaId: consumer.personaId,
    personaName: consumer.personaName,
    platform: consumer.platform,
    currentReferencedVersion: consumer.currentReferencedVersion,
    previousReferencedVersion: consumer.previousReferencedVersion,
    latestPublishedVersion: consumer.latestPublishedVersion,
    rollbackToBuiltinDefault: consumer.rollbackToBuiltinDefault,
  };
}

function mutationPreview({
  kind,
  title,
  available,
  method,
  endpoint,
  body,
  evidence,
  contractGap,
  candidates = [],
  followUp,
}: {
  kind: string;
  title: string;
  available: boolean;
  method: PromptMutationPreview['method'];
  endpoint: string;
  body: Record<string, unknown>;
  evidence: string;
  contractGap: string;
  candidates?: PromptEvidenceCandidate[];
  followUp?: PromptMutationPreview['followUp'];
}): PromptMutationPreview {
  const bodyJson = redactedJson(body, { redactPromptContent: true });
  return {
    kind,
    title,
    available,
    confirmationKey: `${method} ${endpoint}\n${bodyJson}`,
    method,
    endpoint,
    body,
    bodyJson,
    evidence,
    contractGap,
    candidates,
    followUp,
  };
}

function selectCandidates(candidates: PromptEvidenceCandidate[], selectedKeys?: string[]) {
  if (selectedKeys === undefined) return candidates;
  const keySet = new Set(selectedKeys.filter(Boolean));
  return candidates.filter(candidate => keySet.has(candidate.key));
}

function targetsFromCandidates(candidates: PromptEvidenceCandidate[]) {
  return candidates.map(candidate => ({
    persona_id: candidate.personaId,
    persona_name: candidate.personaName,
    platform: candidate.platform,
  }));
}

function unavailableAssignmentPreview(model: PromptAssignmentModel, kind: string, contractGap: string, copy: PromptControlPlaneCopy): PromptMutationPreview {
  return mutationPreview({
    kind,
    title: copy.actions.unavailable,
    available: false,
    method: 'PUT',
    endpoint: `/persona_configs/${encodeURIComponent(model.personaId)}/prompt_module_assignments`,
    body: { updates: [], actor: 'console' },
    evidence: contractGap,
    contractGap,
  });
}

function numericDraftRevision(model: PromptAssignmentModel) {
  return getNumber(model.draftRevision);
}

function assignmentAction(module: PromptAssignmentModuleRow, nextEnabled: boolean) {
  if (module.origin === 'builtin') return nextEnabled ? 'enable_builtin' : 'disable_builtin';
  if (module.origin !== 'managed') return '';
  if (module.assignmentSource === 'available') return nextEnabled ? 'assign' : '';
  return nextEnabled ? 'enable' : 'disable';
}

function assignmentSourceStateCode(source: Record<string, unknown>, platform: string): PromptAssignmentSourceStateCode {
  if (!platform) return 'baseline';
  if (getBool(source.override_present) || getString(source.source) === 'platform_override') return 'platform_override';
  if (getBool(source.inherited)) return 'inherited';
  return 'platform_assignment';
}

function assignmentSourceStateLabel(code: PromptAssignmentSourceStateCode, copy: PromptControlPlaneCopy) {
  switch (code) {
    case 'baseline':
      return copy.assignments.sourceStates.baseline;
    case 'inherited':
      return copy.assignments.sourceStates.inheritedBaseline;
    case 'platform_override':
      return copy.assignments.sourceStates.platformOverride;
    case 'platform_assignment':
      return copy.assignments.sourceStates.platformAssignment;
  }
}

function assignmentSourceEvidence(source: Record<string, unknown>, copy: PromptControlPlaneCopy) {
  const storage = getString(source.storage) || 'prompt_modules';
  const count = getNumber(source.module_count);
  const inherited = getBool(source.inherited) ? copy.modelEvidence.inherited : copy.modelEvidence.direct;
  return copy.modelEvidence.storageEvidence(storage, count, inherited);
}

function findModuleManifest(moduleList: unknown, moduleId: string) {
  const list = asRecord(moduleList);
  const managed = getArray(list.prompt_modules).map(item => asRecord(item));
  const builtins = getArray(list.builtin_prompt_modules).map(item => asRecord(item));
  const managedMatch = managed.find(module => getString(module.id) === moduleId || getString(module.name) === moduleId);
  if (managedMatch) return managedMatch;
  const builtinMatch = builtins.find(module => getString(module.name) === moduleId || getString(asRecord(module.override).artifact_id) === moduleId);
  if (!builtinMatch) return {};
  const override = asRecord(builtinMatch.override);
  if (getString(override.artifact_id) === moduleId) {
    return {
      id: getString(override.artifact_id),
      name: getString(override.name) || getString(builtinMatch.name),
      display_name: getString(builtinMatch.display_name),
      status: getString(override.status) || getString(override.state),
      override_target: getString(builtinMatch.name),
      published_version: getNumber(override.version),
    };
  }
  return builtinMatch;
}

function detailClassification(manifest: Record<string, unknown>, moduleId: string): PromptModuleRow['classification'] {
  const name = getString(manifest.name) || moduleId;
  if (name === 'core' || getString(asRecord(manifest.policy).scope) === 'core') return 'core';
  if (getString(manifest.override_target)) return 'managed override';
  if (getBool(manifest.builtin)) return 'builtin default';
  return 'managed';
}

function latestPublishedVersion(manifest: Record<string, unknown>, versions: Array<Record<string, unknown>>, consumers: PromptConsumerRow[]) {
  const manifestVersion = getNumber(manifest.published_version);
  const versionMax = versions.reduce((max, version) => Math.max(max, getNumber(version.version)), 0);
  const consumerMax = consumers.reduce((max, consumer) => Math.max(max, consumer.latestPublishedVersion), 0);
  return Math.max(manifestVersion, versionMax, consumerMax);
}

function runtimeState(runtimeVersion: number, latestVersion: number, applied: boolean, copy: PromptControlPlaneCopy) {
  if (latestVersion > 0 && runtimeVersion > 0 && runtimeVersion < latestVersion) return copy.modelEvidence.runtimeBehind;
  if (applied) return copy.modelEvidence.runtimeApplied;
  if (runtimeVersion > 0) return copy.modelEvidence.runtimeVersion(runtimeVersion);
  return copy.modelEvidence.runtimeUnavailable;
}

function classificationEvidence(classification: PromptModuleRow['classification'], copy: PromptControlPlaneCopy) {
  switch (classification) {
    case 'core':
      return copy.modelEvidence.coreReadable;
    case 'builtin default':
      return copy.modelEvidence.builtinDefault;
    case 'managed override':
      return copy.modelEvidence.managedOverride;
    case 'managed':
      return copy.modelEvidence.managedArtifact;
  }
}

function validationSummary(value: unknown, copy: PromptControlPlaneCopy) {
  const validation = asRecord(value);
  if (getBool(validation.valid)) return copy.modelEvidence.valid;
  const errors = getArray(validation.errors);
  if (errors.length) return copy.modelEvidence.validationIssues(errors.length);
  return copy.common.unavailable;
}

function previewAvailable(preview: Record<string, unknown>) {
  return Object.keys(preview).length > 0;
}

function extractPlatforms(value: unknown, selectedPlatform: string) {
  const platforms = filterRealPersonaPlatforms(getArray(value).map(item => {
    if (typeof item === 'string') return item;
    return asRecord(item).name;
  }));
  if (selectedPlatform && !platforms.includes(selectedPlatform)) {
    platforms.push(selectedPlatform);
  }
  return platforms;
}


function formatDisplayName(name: string) {
  return name
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Unnamed';
}

function firstRecord(...records: Array<Record<string, unknown>>) {
  return records.find(record => Object.keys(record).length > 0) ?? {};
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function getString(value: unknown) {
  if (value === undefined || value === null) return '';
  return String(value);
}

function getNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function getBool(value: unknown) {
  return value === true || value === 'true';
}

export function redactedPromptJson(value: unknown) {
  return redactedJson(value);
}
